import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  FileText,
  Database,
  Zap,
  Check,
  Shield,
  Users,
  BarChart3,
  FileCheck,
  Landmark,
  TrendingUp,
  Building2,
} from "lucide-react";
import FAQ from "@/components/ui/FAQ";
import ImpactSection from "@/components/ui/ImpactSection";
import UseCasesSection from "@/components/ui/UseCasesSection";
import KeyFeaturesSection from "@/components/ui/KeyFeatureSection";
import CTASection from "@/components/ui/CtaSection";

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 },
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1,
    },
  },
};
const faqData = [
  {
    question: "What is Bridge Format AIQ?",
    answer:
      "Bridge Format AIQ is an AI-powered document intelligence platform that converts unstructured documents—such as PDFs, forms, reports, and contracts—into structured, usable data. It helps businesses automate document processing, reduce manual work, and integrate extracted data directly into their existing systems.",
  },
  {
    question: "What types of documents can Bridge Format AIQ process?",
    answer:
      "Bridge Format AIQ can process a wide variety of business documents, including invoices and purchase orders, medical reports and patient records, insurance claim documents, contracts and legal agreements, HR forms and resumes, and supplier and logistics documents. The platform supports PDFs, scanned files, images, and digital forms.",
  },
  {
    question: "How accurate is the data extraction?",
    answer:
      "Bridge Format AIQ uses advanced AI and machine learning models to achieve up to 99% data extraction accuracy. The system continuously improves as it processes more documents, ensuring consistent and reliable results for business workflows.",
  },
  {
    question: "How does Bridge Format AIQ work?",
    answer:
      "The platform follows a simple four-step process: Upload or connect documents from email, cloud storage, APIs, or enterprise systems. The AI analyzes the document and understands its structure and content. Key information is extracted and converted into structured data. The data is then exported or integrated into your business systems like CRM, ERP, or databases.",
  },
  {
    question: "Can Bridge Format AIQ integrate with our existing systems?",
    answer:
      "Yes. Bridge Format AIQ is designed to integrate easily with your existing technology stack. Structured data can be exported to CRMs, ERPs, databases, analytics platforms, or other internal systems through APIs and integrations.",
  },
  {
    question: "Is Bridge Format AIQ secure?",
    answer:
      "Yes. Bridge Format AIQ is built with enterprise-grade security standards to protect sensitive business data. The platform follows strict security protocols and compliance practices to ensure safe document processing and data management.",
  },
];
const useCases = [
  {
    icon: FileText,
    title: "Healthcare",
    desc: "Automate extraction of data from medical reports, insurance claims, patient records, and healthcare forms to improve efficiency and reduce administrative workload. ",
  },
  {
    icon: Users,
    title: "Insurance",
    desc: "Streamline claims processing, policy documentation, and risk assessment by automatically extracting key information from insurance documents. ",
  },
  {
    icon: Shield,
    title: "Legal",
    desc: "Organize and analyze contracts, agreements, legal notices, and case documents with intelligent data extraction and structured outputs. ",
  },
  {
    icon: BarChart3,
    title: "Human Resources",
    desc: "Simplify employee record management, onboarding documents, resumes, and compliance paperwork with automated document processing.",
  },
  {
    icon: BarChart3,
    title: "Procurement & Supply Chain",
    desc: "Manage invoices, purchase orders, supplier contracts, and logistics documents by converting them into structured data for faster operations.",
  },
  {
    id: 6,
    title: "Banking & Finance ",
    desc: "Automate processing of financial documents such as bank statements, loan applications, and KYC records to improve accuracy, reduce manual effort, and accelerate decision-making. ",
    icon: Landmark,
  },
  {
    id: 7,
    title: "Sales",
    desc: "Streamline sales operations by extracting data from proposals, contracts, and invoices, enabling faster document creation, processing, and deal closures. ",
    icon: TrendingUp,
  },
  {
    id: 8,
    title: "Commercial Real Estate",
    desc: "Simplify handling of leases, property documents, and agreements by converting unstructured data into organized insights for faster transactions and better portfolio management. ",
    icon: Building2,
  },
];

const impactPoints = [
  "Up to 70% reduction in document normalization effort",
  "Standardized outputs across vendors and regions",
  "Faster downstream processing",
  "Reduced compliance exposure",
  "Format governance at enterprise scale",
];
export const Home = () => {
  return (
    <div className="pt-16 md:pt-20" data-testid="home-page">
      {/* Hero Section */}
      <section
        className="hero-texture min-h-[70vh] flex items-center section-padding"
        data-testid="hero-section"
      >
        <div className="container-width relative z-10">
          <motion.div
            className="max-w-6xl"
            initial="initial"
            animate="animate"
            variants={staggerContainer}
          >
            {/* <motion.p className="label-mono mb-6 " variants={fadeInUp}>
              Enterprise Format Intelligence
            </motion.p> */}

            <motion.h1
              className="text-2xl md:text-5xl font-bold tracking-tight leading-[1.1] text-neutral-950 mb-8"
              style={{ fontFamily: "Manrope, sans-serif" }}
              variants={fadeInUp}
              data-testid="hero-headline"
            >
              Create & Process Documents 10x Faster
            </motion.h1>

            <motion.p
              className="text-lg md:text-xl leading-relaxed text-neutral-600 max-w-2xl mb-10"
              variants={fadeInUp}
            >
              Bridge Format AIQ is an AI-powered platform that converts
              unstructured documents into structured data, helping businesses
              automate workflows, reduce manual work, and make faster decisions.
            </motion.p>

            <motion.div
              className="flex flex-col sm:flex-row gap-4"
              variants={fadeInUp}
            >
              <Link
                to="/contact-us"
                className="btn-primary gap-2 btn-hover-scale"
                data-testid="hero-demo-btn"
              >
                Contact us
                <ArrowRight className="w-4 h-4" />
              </Link>
              <button
                onClick={() => {
                  document
                    .getElementById("how-it-works")
                    ?.scrollIntoView({ behavior: "smooth" });
                }}
                className="btn-secondary gap-2 btn-hover-scale"
              >
                See How It Works
              </button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Impact Section */}
      <ImpactSection />

      {/* Problem Statement Section */}
      {/* <section
        className="section-padding bg-neutral-50"
        data-testid="problem-section"
      >
        <div className="container-width">
          <motion.div
            className="max-w-3xl"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="label-mono mb-6">The Problem</p>
            <h2
              className="text-3xl md:text-5xl font-semibold tracking-tight leading-tight text-neutral-950 mb-8"
              style={{ fontFamily: "Manrope, sans-serif" }}
            >
              Your Systems Are Structured. Your Documents Are Not.
            </h2>
            <div className="text-lg leading-relaxed text-neutral-600 space-y-6">
              <p>
                Every enterprise runs on structured systems — ERP, CRM,
                compliance platforms.
              </p>
              <p>But the documents feeding them are chaotic:</p>
              <ul className="space-y-3 ml-6">
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 bg-neutral-950 rounded-full mt-2.5 flex-shrink-0" />
                  <span>Vendor-specific layouts</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 bg-neutral-950 rounded-full mt-2.5 flex-shrink-0" />
                  <span>Region-specific variations</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 bg-neutral-950 rounded-full mt-2.5 flex-shrink-0" />
                  <span>Inconsistent formatting</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 bg-neutral-950 rounded-full mt-2.5 flex-shrink-0" />
                  <span>Semi-structured or scanned files</span>
                </li>
              </ul>
              <p className="font-medium text-neutral-950">
                Manual normalization becomes the hidden operational tax. Digital
                is not standardized.
              </p>
            </div>
          </motion.div>
        </div>
      </section> */}

      {/* Category Definition Section */}
      {/* <section className="section-padding" data-testid="category-section">
        <div className="container-width">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="label-mono mb-6">The Category</p>
            <h2
              className="text-3xl md:text-5xl font-semibold tracking-tight leading-tight text-neutral-950 mb-6"
              style={{ fontFamily: "Manrope, sans-serif" }}
            >
              Not OCR. Not Automation. Infrastructure.
            </h2>
          </motion.div>

          <motion.div
            className="grid grid-cols-1 md:grid-cols-3 gap-0"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="compare-column">
              <h3 className="text-sm font-mono uppercase tracking-wider text-neutral-500 mb-4">
                OCR
              </h3>
              <p className="text-neutral-600">
                Extracts fields from documents. Limited understanding. No format
                intelligence.
              </p>
            </div>
            <div className="compare-column">
              <h3 className="text-sm font-mono uppercase tracking-wider text-neutral-500 mb-4">
                Automation
              </h3>
              <p className="text-neutral-600">
                Moves workflows. Routes documents. Does not address format
                chaos.
              </p>
            </div>
            <div className="compare-column-highlight">
              <h3 className="text-sm font-mono uppercase tracking-wider text-neutral-400 mb-4">
                Format Intelligence
              </h3>
              <p className="text-neutral-200">
                Enforces format governance. Reconstructs structure. Produces
                reliable system-ready documents.
              </p>
            </div>
          </motion.div>

          <motion.p
            className="text-center text-lg text-neutral-600 mt-12 max-w-2xl mx-auto"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            This is the missing intelligence layer between document intake and
            enterprise systems.
          </motion.p>
        </div>
      </section> */}

      {/* How It Works Section */}
      <section
        className="section-padding bg-neutral-50"
        data-testid="how-it-works-section"
        id="how-it-works"
      >
        <div className="container-width">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            {/* <p className="label-mono mb-6">The Process</p> */}
            <h2
              className="text-3xl md:text-5xl font-semibold tracking-tight leading-tight text-neutral-950"
              style={{ fontFamily: "Manrope, sans-serif" }}
            >
              How It Works? 
            </h2>
          </motion.div>

          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {[
              {
                step: "01",
                title: "Upload or Connect",
                desc: "Import documents from emails, cloud storage, APIs, or enterprise systems.",
                icon: FileText,
              },
              {
                step: "02",
                title: "AI Understands the Document ",
                desc: "Bridge Format AIQ analyzes the content and identifies relevant information from complex documents.",
                icon: Zap,
              },
              {
                step: "03",
                title: "Extract & Structure Data",
                desc: "The platform extracts key fields, organizes them, and validates the data.",
                icon: FileCheck,
              },
              {
                step: "04",
                title: "Deliver",
                desc: "Structured data can be exported to CRMs, ERPs, databases, or analytics platforms.",
                icon: Database,
              },
            ].map((item) => (
              <motion.div
                key={item.step}
                className="feature-card rounded-[4px] group "
                variants={fadeInUp}
              >
                <div className="flex items-center gap-4 mb-0">
                  <span className="text-xs font-mono text-neutral-400">
                    {item.step}
                  </span>
                  <item.icon className="w-5 h-5 text-neutral-950 group-hover:text-primary" />
                </div>
                <h3
                  className="text-xl font-semibold text-neutral-950 my-3"
                  style={{ fontFamily: "Manrope, sans-serif" }}
                >
                  {item.title}
                </h3>
                <p className="text-neutral-600 text-sm leading-relaxed">
                  {item.desc}
                </p>
              </motion.div>
            ))}
          </motion.div>

          {/* <motion.p
            className="text-center text-sm font-mono text-neutral-500 mt-12"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            No manual reformatting. No repeated correction cycles.
          </motion.p> */}
        </div>
      </section>

      {/* Use Case Section */}
      <UseCasesSection />

      {/* Key Features Section */}
      <KeyFeaturesSection />
      {/* Business Impact Section */}
      {/* <section className="section-padding" data-testid="impact-section">
        <div className="container-width">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <p className="label-mono mb-6">The Impact</p>
              <h2
                className="text-3xl md:text-5xl font-semibold tracking-tight leading-tight text-neutral-950 mb-8"
                style={{ fontFamily: "Manrope, sans-serif" }}
              >
                Built for Operational Scale.
              </h2>
              <ul className="space-y-4">
                {impactPoints.map((point, index) => (
                  <motion.li
                    key={index}
                    className="flex items-start gap-3"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                  >
                    <Check className="w-5 h-5 text-neutral-950 mt-0.5 flex-shrink-0" />
                    <span className="text-neutral-600">{point}</span>
                  </motion.li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              className="texture-bg p-12 border border-neutral-200 relative"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="relative z-10">
                <div
                  className="text-6xl md:text-8xl font-bold text-neutral-950 mb-4"
                  style={{ fontFamily: "Manrope, sans-serif" }}
                >
                  70%
                </div>
                <p className="text-lg text-neutral-600">
                  Reduction in document normalization effort
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section> */}

      {/* Use Case Snapshot Section */}
      <section
        className="section-padding bg-secondary text-white"
        data-testid="usecase-snapshot-section"
      >
        <div className="container-width">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            {/* <p className="text-xs font-mono uppercase tracking-wider text-white/80 mb-6">
              Applications
            </p> */}
            <h2
              className="text-3xl md:text-5xl font-semibold tracking-tight leading-tight"
              style={{ fontFamily: "Manrope, sans-serif" }}
            >
              Industries We Serve
            </h2>
          </motion.div>

          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {useCases.map((useCase) => (
              <motion.div
                key={useCase.title}
                className="border rounded-[4px] border-white p-8 hover:border-white/80 transition-colors"
                variants={fadeInUp}
              >
                <useCase.icon className="w-8 h-8 text-white mb-6" />
                <h3
                  className="text-lg font-semibold mb-2"
                  style={{ fontFamily: "Manrope, sans-serif" }}
                >
                  {useCase.title}
                </h3>
                <p className="text-white/80 text-sm">{useCase.desc}</p>
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            className="text-center mt-12"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <Link
              to="/who-we-serve"
              className="btn-secondary gap-2 btn-hover-scale"
              data-testid="explore-usecases-btn"
            >
              Explore Use Cases
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Final CTA Section */}
      {/* <section className="section-padding" data-testid="final-cta-section">
        <div className="container-width">
          <motion.div
            className="text-center max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2
              className="text-3xl md:text-5xl font-semibold tracking-tight leading-tight text-neutral-950 mb-6"
              style={{ fontFamily: "Manrope, sans-serif" }}
            >
              Standardize Before You Scale.
            </h2>
            <p className="text-lg text-neutral-600 mb-10">
              Bring control to document-driven operations.
            </p>
            <Link
              to="/contact-us"
              className="btn-primary gap-2 btn-hover-scale"
              data-testid="final-demo-btn"
            >
              Request Enterprise Demo
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section> */}
      <FAQ data={faqData} />
      <CTASection />
    </div>
  );
};

export default Home;
